import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { userApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const toInitCap = (str) =>
  str ? str.split(/[\s_]+/).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '';

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const STATUS_STYLE = {
  draft:        { bg: '#f3f4f6', color: '#6b7280',  label: 'Draft' },
  registration: { bg: '#dbeafe', color: '#1d4ed8',  label: 'Registration Open' },
  seeding:      { bg: '#fef3c7', color: '#b45309',  label: 'Seeding' },
  in_progress:  { bg: '#d1fae5', color: '#065f46',  label: 'In Progress' },
  completed:    { bg: '#e5e7eb', color: '#374151',  label: 'Completed' },
};

const getTournamentPeriod = (t) => {
  const now = new Date();
  const start = t.start_date ? new Date(t.start_date) : null;
  const end   = t.end_date   ? new Date(t.end_date)   : null;
  if (t.status === 'completed' || (end && end < now)) return 'past';
  if (start && start > now) return 'upcoming';
  return 'current';
};

const PERIOD_LABEL = { current: 'Current', upcoming: 'Upcoming', past: 'Past' };
const PERIOD_ORDER = ['current', 'upcoming', 'past'];

// ─── Shared edit-details form ─────────────────────────────────────────────────

const EditDetailsCard = ({ profileUser, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [name,  setName]  = useState(profileUser.name  || '');
  const [phone, setPhone] = useState(profileUser.phone || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await userApi.updateProfile({ name: name.trim(), phone: phone.trim() || null });
      toast.success('Profile updated');
      setEditing(false);
      onSaved();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>My Details</h3>
        {!editing && (
          <button className="btn btn-outline btn-sm" onClick={() => setEditing(true)}>Edit</button>
        )}
      </div>

      {editing ? (
        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: 400 }}>
          <div className="form-group">
            <label className="form-label">Name *</label>
            <input className="form-input" value={name} onChange={e => setName(e.target.value)} required />
          </div>
          <div className="form-group">
            <label className="form-label">Mobile</label>
            <input
              className="form-input" type="tel" placeholder="e.g. +91 98765 43210"
              value={phone} onChange={e => setPhone(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.9rem' }}>
          <div><span style={{ color: '#9ca3af', minWidth: 80, display: 'inline-block' }}>Name</span>   {profileUser.name}</div>
          <div><span style={{ color: '#9ca3af', minWidth: 80, display: 'inline-block' }}>Email</span>  {profileUser.email}</div>
          <div><span style={{ color: '#9ca3af', minWidth: 80, display: 'inline-block' }}>Mobile</span> {profileUser.phone || '—'}</div>
          <div><span style={{ color: '#9ca3af', minWidth: 80, display: 'inline-block' }}>Role</span>   {toInitCap(profileUser.role)}</div>
        </div>
      )}
    </div>
  );
};

// ─── Shared tournament list (grouped by period) ───────────────────────────────

const TournamentList = ({ tournaments, emptyMessage }) => {
  const grouped = { current: [], upcoming: [], past: [] };
  (tournaments || []).forEach(t => grouped[getTournamentPeriod(t)].push(t));
  const hasAny = (tournaments || []).length > 0;

  if (!hasAny) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">🏸</div>
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return PERIOD_ORDER.map(period => {
    const list = grouped[period];
    if (list.length === 0) return null;
    return (
      <div key={period} style={{ marginBottom: '1.25rem' }}>
        <div style={{
          fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.07em', color: '#9ca3af', marginBottom: '0.5rem',
        }}>
          {PERIOD_LABEL[period]}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {list.map(t => {
            const st = STATUS_STYLE[t.status] || STATUS_STYLE.draft;
            return (
              <div key={t.tournament_id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0.65rem 0.9rem', borderRadius: 8, border: '1px solid #e5e7eb',
                background: '#fafafa', flexWrap: 'wrap', gap: '0.5rem',
              }}>
                <div>
                  <Link
                    to={`/tournaments/${t.tournament_id}`}
                    style={{ fontWeight: 600, color: '#0D9488', textDecoration: 'none', fontSize: '0.9rem' }}
                  >
                    {t.tournament_name}
                  </Link>
                  <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: 2 }}>
                    {toInitCap(t.sport_type)}
                    {t.start_date ? ` · ${formatDate(t.start_date)}` : ''}
                    {t.end_date ? ` – ${formatDate(t.end_date)}` : ''}
                  </div>
                </div>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', padding: '2px 10px',
                  borderRadius: 999, fontSize: '0.72rem', fontWeight: 600,
                  background: st.bg, color: st.color, whiteSpace: 'nowrap',
                }}>
                  {st.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  });
};

// ─── License & Subscription card ─────────────────────────────────────────────

const LicenseCard = ({ profileUser, onLicenseRenewed }) => {
  const u = profileUser;
  const now = new Date();
  const { licenseExpired, setLicenseExpired } = useAuth();
  const isElectron = typeof window !== 'undefined' && !!window.electronAPI;

  // In the desktop app the local SQLite DB doesn't store license fields —
  // read them from the Electron license cache instead (authoritative source).
  const [electronLicense, setElectronLicense] = useState(null);
  useEffect(() => {
    if (!isElectron) return;
    window.electronAPI.getLicenseStatus().then(setElectronLicense).catch(() => {});
  }, [isElectron]);

  // Derive display values: prefer Electron IPC data on desktop, fall back to profile API
  const desktopActive  = isElectron ? !!(electronLicense?.valid || electronLicense?.readOnly || licenseExpired) : !!u.desktop_enabled;
  const desktopExpiry  = isElectron
    ? (electronLicense?.expiresAt ? new Date(electronLicense.expiresAt) : null)
    : (u.desktop_license_expires_at ? new Date(u.desktop_license_expires_at) : null);
  const desktopGranted = isElectron
    ? (electronLicense?.issuedAt ? new Date(electronLicense.issuedAt) : null)
    : null;
  const desktopExpired = isElectron ? licenseExpired : (desktopExpiry && desktopExpiry < now);

  const planLabel = u.role === 'admin' ? 'Club (Admin)' : toInitCap(u.subscription_status || 'free');

  const [rechecking, setRechecking] = useState(false);
  const [recheckMsg, setRecheckMsg] = useState('');

  const handleRecheck = async () => {
    setRechecking(true);
    setRecheckMsg('');
    try {
      const status = await window.electronAPI.recheckLicense();
      if (status.valid) {
        setLicenseExpired(false);
        setRecheckMsg('License renewed! Full access restored.');
        if (onLicenseRenewed) onLicenseRenewed();
      } else {
        setRecheckMsg('License is still expired. Contact your administrator.');
      }
    } catch (err) {
      const msg = (err.message || 'Re-check failed').replace(/^Error invoking remote method '[^']+': Error:\s*/i, '');
      setRecheckMsg(msg);
    } finally {
      setRechecking(false);
    }
  };

  const Row = ({ label, value, valueStyle }) => (
    <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.875rem', padding: '6px 0', borderBottom: '1px solid #f3f4f6' }}>
      <span style={{ color: '#9ca3af', minWidth: 160 }}>{label}</span>
      <span style={{ color: '#111827', fontWeight: 500, ...valueStyle }}>{value}</span>
    </div>
  );

  return (
    <div className="card" style={{ marginBottom: '1.5rem', border: licenseExpired ? '1px solid #fca5a5' : undefined }}>
      <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600 }}>License & Subscription</h3>

      {licenseExpired && isElectron && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem' }}>
          <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: '0.5rem' }}>
            ⚠️ Your desktop license has expired
          </div>
          <p style={{ fontSize: '0.82rem', color: '#7f1d1d', margin: '0 0 0.75rem' }}>
            Ask your administrator to renew your license on the TournNext web panel, then click Re-check below to restore full access.
          </p>
          <button
            onClick={handleRecheck}
            disabled={rechecking}
            style={{ padding: '6px 16px', fontSize: '0.82rem', fontWeight: 600, background: '#dc2626', color: 'white', border: 'none', borderRadius: 6, cursor: rechecking ? 'not-allowed' : 'pointer' }}
          >
            {rechecking ? 'Checking…' : '↺ Re-check License'}
          </button>
          {recheckMsg && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: recheckMsg.includes('renewed') ? '#16a34a' : '#dc2626', fontWeight: 500 }}>
              {recheckMsg}
            </div>
          )}
        </div>
      )}

      <div style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af', marginBottom: '0.4rem' }}>
          Web App
        </div>
        <Row label="Plan" value={planLabel} />
        <Row label="Member since" value={formatDate(u.created_at)} />
      </div>

      <div>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af', marginBottom: '0.4rem' }}>
          Desktop App
        </div>
        {desktopActive ? (
          <>
            <Row label="Status"
              value={desktopExpired ? 'Expired' : 'Active'}
              valueStyle={{ color: desktopExpired ? '#dc2626' : '#059669' }} />
            {desktopGranted && (
              <Row label="Granted on" value={formatDate(desktopGranted)} />
            )}
            <Row
              label="Expires"
              value={desktopExpiry
                ? (desktopExpired ? `Expired on ${formatDate(desktopExpiry)}` : formatDate(desktopExpiry))
                : 'No expiry set'}
              valueStyle={desktopExpired ? { color: '#dc2626' } : {}}
            />
            {!isElectron && (
              <Row label="Devices allowed" value={u.desktop_devices_allowed || 1} />
            )}
          </>
        ) : (
          <Row label="Status" value="Not granted — contact your administrator"
            valueStyle={{ color: '#9ca3af', fontWeight: 400 }} />
        )}
      </div>
    </div>
  );
};

// ─── Umpire / Moderator profile ───────────────────────────────────────────────

const StaffProfile = ({ profile, onSaved }) => (
  <>
    <EditDetailsCard profileUser={profile.user} onSaved={onSaved} />
    <LicenseCard profileUser={profile.user} />
    <div className="card">
      <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>Tournament Assignments</h3>
      <TournamentList tournaments={profile.staff_assignments} emptyMessage="No tournament assignments yet." />
    </div>
  </>
);

// ─── Organizer profile ────────────────────────────────────────────────────────

const OrganizerProfile = ({ profile, onSaved }) => {
  const staff = profile.invited_staff || [];

  // Group staff by tournament for display
  const byTournament = {};
  staff.forEach(s => {
    if (!byTournament[s.tournament_id]) {
      byTournament[s.tournament_id] = { name: s.tournament_name, status: s.tournament_status, members: [] };
    }
    byTournament[s.tournament_id].members.push(s);
  });

  return (
    <>
      <EditDetailsCard profileUser={profile.user} onSaved={onSaved} />
      <LicenseCard profileUser={profile.user} />

      {/* Owned tournaments */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>My Tournaments</h3>
          <Link to="/create-tournament" className="btn btn-primary btn-sm">+ New</Link>
        </div>
        <TournamentList tournaments={profile.owned_tournaments} emptyMessage="No tournaments created yet." />
      </div>

      {/* Invited staff */}
      <div className="card">
        <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>Invited Staff</h3>
        {staff.length === 0 ? (
          <div className="empty-state">
            <p>No staff invited yet. Open a tournament and go to the Staff tab to invite umpires and moderators.</p>
          </div>
        ) : (
          Object.entries(byTournament).map(([tid, group]) => {
            const st = STATUS_STYLE[group.status] || STATUS_STYLE.draft;
            return (
              <div key={tid} style={{ marginBottom: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Link to={`/tournaments/${tid}`} style={{ fontWeight: 600, color: '#374151', fontSize: '0.875rem', textDecoration: 'none' }}>
                    {group.name}
                  </Link>
                  <span style={{
                    padding: '1px 8px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 600,
                    background: st.bg, color: st.color,
                  }}>
                    {st.label}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', paddingLeft: '0.75rem' }}>
                  {group.members.map(m => (
                    <div key={m.user_id} style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem',
                      fontSize: '0.85rem', color: '#374151',
                    }}>
                      <span style={{
                        padding: '1px 8px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 600,
                        background: m.staff_role === 'umpire' ? '#CCFBF1' : '#e0f2fe',
                        color: m.staff_role === 'umpire' ? '#5b21b6' : '#0369a1',
                      }}>
                        {toInitCap(m.staff_role)}
                      </span>
                      <span>{m.staff_name}</span>
                      <span style={{ color: '#9ca3af', fontSize: '0.78rem' }}>{m.staff_email}</span>
                      {m.staff_phone && <span style={{ color: '#9ca3af', fontSize: '0.78rem' }}>{m.staff_phone}</span>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
};

// ─── Player profile ───────────────────────────────────────────────────────────

const PlayerProfile = ({ profile }) => {
  const { stats, participations } = profile;

  const statCards = [
    { label: 'Tournaments', value: stats.tournaments_played, icon: '🏆' },
    { label: 'Matches Played', value: stats.total_matches,   icon: '🎯' },
    { label: 'Wins',          value: stats.wins,             icon: '✅' },
    { label: 'Win Rate',      value: `${stats.win_rate}%`,   icon: '📈' },
  ];

  return (
    <>
      <div className="grid grid-4" style={{ marginBottom: '1.5rem' }}>
        {statCards.map(({ label, value, icon }) => (
          <div key={label} className="card" style={{ textAlign: 'center', padding: '1.5rem 1rem' }}>
            <div style={{ fontSize: '1.25rem', marginBottom: '0.4rem' }}>{icon}</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#0D9488', lineHeight: 1 }}>{value}</div>
            <div style={{ color: '#9ca3af', fontSize: '0.8rem', marginTop: '0.35rem', fontWeight: 500 }}>{label}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>Tournament History</h3>
        {participations.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🏆</div>
            <h3>No tournaments yet</h3>
            <p>Register for a tournament to track your history here.</p>
            <Link to="/tournaments" className="btn btn-primary" style={{ marginTop: '0.75rem' }}>
              Browse Tournaments
            </Link>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Tournament</th><th>Sport</th><th>Format</th>
                  <th>Date</th><th>Category</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {participations.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <Link to={`/tournaments/${p.tournament_id}`} style={{ color: '#0D9488', fontWeight: 500 }}>
                        {p.tournament_name}
                      </Link>
                    </td>
                    <td>{toInitCap(p.sport_type)}</td>
                    <td>{toInitCap(p.draw_type)}</td>
                    <td>{formatDate(p.start_date)}</td>
                    <td style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                      {[p.event_type, p.event_category, p.age_category].filter(Boolean).join(' · ')}
                    </td>
                    <td>
                      <span className={`badge ${
                        p.tournament_status === 'completed' ? 'badge-success' :
                        p.tournament_status === 'in_progress' ? 'badge-warning' : 'badge-info'
                      }`}>
                        {toInitCap(p.tournament_status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

// ─── Main Profile page ────────────────────────────────────────────────────────

const Profile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  const loadProfile = () => {
    setLoading(true);
    userApi.getProfile()
      .then((res) => setProfile(res.data))
      .catch(() => setError('Failed to load profile'))
      .finally(() => setLoading(false));
  };

  useEffect(loadProfile, []);

  if (loading) return <div className="container"><div className="loading"><div className="spinner" /></div></div>;
  if (error)   return <div className="container"><div className="alert alert-error">{error}</div></div>;

  const role = user?.role;

  return (
    <div className="container">
      {/* Profile header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '1.25rem',
        marginBottom: '2rem', paddingBottom: '2rem', borderBottom: '1px solid #f0f0f0',
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: 'linear-gradient(135deg, #0D9488, #0F766E)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1.6rem', fontWeight: 700, color: 'white', flexShrink: 0,
        }}>
          {(user?.name || 'U').charAt(0).toUpperCase()}
        </div>
        <div>
          <h1 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#111827', margin: 0 }}>{user?.name}</h1>
          <p style={{ color: '#9ca3af', fontSize: '0.875rem', margin: '4px 0 0' }}>
            {user?.email} &middot; <span style={{ color: '#6b7280' }}>{toInitCap(role)}</span>
          </p>
        </div>
      </div>

      {['umpire', 'moderator'].includes(role) && (
        <StaffProfile profile={profile} onSaved={loadProfile} />
      )}
      {['organizer', 'admin'].includes(role) && (
        <OrganizerProfile profile={profile} onSaved={loadProfile} />
      )}
      {!['umpire', 'moderator', 'organizer', 'admin'].includes(role) && (
        <PlayerProfile profile={profile} />
      )}
    </div>
  );
};

export default Profile;
